import { apiUrl } from "../../api/endpoints";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Spinner from "../../Components/Spinner";

import { useAuth } from "../../hooks/AuthProvider";
import { LanguageContext } from "../../context/LanguageContext";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TextField from "@mui/material/TextField";
import "./UserHealthDetails.css";
import {
  Field,
  HealthPageHeader,
  PermissionDenied,
  Section,
  SummaryCard,
  formatDateParam,
  formatDateTime,
  formatPercent,
  getBMIColor,
  getHealthTranslations,
  isRtlLanguage,
  roundValue,
} from "./userHealthShared";

const COLORS = [
  "var(--health-info-color)",
  "var(--health-success-color)",
  "var(--health-warning-color)",
  "var(--health-danger-color)",
];

const asNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

export const UserHealthDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);

  const { permissions, org } = useAuth();
  const { lang } = useContext(LanguageContext);
  const t = getHealthTranslations(lang);
  const canView = permissions["view_health_dashboard"];
  const isRtl = isRtlLanguage(lang);

  const fetchStats = useCallback(async () => {
    if (!canView || !org?.id) return;

    setLoading(true);
    try {
      const params = {};
      const fromParam = formatDateParam(from);
      const toParam = formatDateParam(to);
      if (fromParam) params.from = fromParam;
      if (toParam) params.to = toParam;

      const res = await axios.get(apiUrl(`user-health/stats-by-org/${org.id}`), { params });
      setData(res.data?.status === "OK" ? res.data.data : null);
    } catch (err) {
      console.error("Stats error:", err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [canView, from, org?.id, to]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const bmiData = useMemo(
    () => [
      { name: t.categories.underweight, value: data?.bmiDistribution?.underweight || 0 },
      { name: t.categories.healthy, value: data?.bmiDistribution?.healthy || 0 },
      { name: t.categories.overweight, value: data?.bmiDistribution?.overweight || 0 },
      { name: t.categories.obese, value: data?.bmiDistribution?.obese || 0 },
    ],
    [data, t]
  );

  const compositionData = useMemo(
    () => [
      { name: t.categories.fat, value: data?.avgFat || 0 },
      { name: t.categories.muscle, value: data?.avgMuscle || 0 },
      { name: t.categories.water, value: data?.avgWater || 0 },
      { name: t.labels.protein, value: data?.avgProtein || 0 },
      { name: t.labels.bone, value: data?.avgBone || 0 },
      { name: t.labels.subFat, value: data?.avgSubFat || 0 },
      { name: t.labels.inFat, value: data?.avgInFat || 0 },
    ],
    [data, t]
  );

  const riskData = useMemo(
    () => [
      { name: t.categories.lowRisk, value: data?.riskSummary?.low || 0 },
      { name: t.categories.mediumRisk, value: data?.riskSummary?.medium || 0 },
      { name: t.categories.highRisk, value: data?.riskSummary?.high || 0 },
    ],
    [data, t]
  );

  const benefitData = useMemo(
    () => [
      { name: t.labels.weightControl, value: asNumber(data?.avgWeightControl) },
      { name: t.labels.fatControl, value: asNumber(data?.avgFatControl) },
      { name: t.labels.muscleControl, value: asNumber(data?.avgMuscleControl) },
      { name: t.labels.idealWeightGap, value: asNumber(data?.idealWeightGap) },
    ],
    [data, t]
  );

  const goalsData = useMemo(
    () => [
      { name: t.labels.aerobic, value: asNumber(data?.avgAerobicGoal) },
      { name: t.labels.anaerobic, value: asNumber(data?.avgAnaGoal) },
      { name: t.labels.endurance, value: asNumber(data?.avgEnduGoal) },
      { name: t.labels.sportGoal, value: asNumber(data?.avgSportGoal) },
    ],
    [data, t]
  );

  const segmentData = useMemo(
    () => [
      {
        name: t.categories.leftArm,
        fat: asNumber(data?.segmentAverages?.leftArm?.fat),
        muscle: asNumber(data?.segmentAverages?.leftArm?.muscle),
      },
      {
        name: t.categories.rightArm,
        fat: asNumber(data?.segmentAverages?.rightArm?.fat),
        muscle: asNumber(data?.segmentAverages?.rightArm?.muscle),
      },
      {
        name: t.categories.leftLeg,
        fat: asNumber(data?.segmentAverages?.leftLeg?.fat),
        muscle: asNumber(data?.segmentAverages?.leftLeg?.muscle),
      },
      {
        name: t.categories.rightLeg,
        fat: asNumber(data?.segmentAverages?.rightLeg?.fat),
        muscle: asNumber(data?.segmentAverages?.rightLeg?.muscle),
      },
      {
        name: t.categories.trunk,
        fat: asNumber(data?.segmentAverages?.trunk?.fat),
        muscle: asNumber(data?.segmentAverages?.trunk?.muscle),
      },
    ],
    [data, t]
  );

  const riskAverageData = useMemo(
    () => [
      { name: t.labels.humpback, value: asNumber(data?.riskAverages?.humpback) },
      { name: t.labels.pelvis, value: asNumber(data?.riskAverages?.pelvis) },
      { name: t.labels.shoulder, value: asNumber(data?.riskAverages?.shoulder) },
      { name: t.labels.knee, value: asNumber(data?.riskAverages?.knee) },
      { name: t.labels.spine, value: asNumber(data?.riskAverages?.spine) },
      { name: t.labels.posture, value: asNumber(data?.riskAverages?.posture) },
      { name: t.labels.frontHead, value: asNumber(data?.riskAverages?.frontHead) },
    ],
    [data, t]
  );

  const scoreData = useMemo(
    () => [
      { name: t.categories.excellent, value: data?.scoreDistribution?.excellent || 0 },
      { name: t.categories.good, value: data?.scoreDistribution?.good || 0 },
      { name: t.categories.needsAttention, value: data?.scoreDistribution?.needsAttention || 0 },
    ],
    [data, t]
  );

  const coverageData = useMemo(
    () => [
      { name: t.categories.bodyComposition, value: data?.availableMetrics?.bodyComposition || 0 },
      { name: t.categories.segmentData, value: data?.availableMetrics?.segmentComposition || 0 },
      { name: t.categories.vitals, value: data?.availableMetrics?.vitals || 0 },
      { name: t.categories.posture, value: data?.availableMetrics?.posture || 0 },
      { name: t.categories.balance, value: data?.availableMetrics?.balance || 0 },
    ],
    [data, t]
  );

  const trendData = useMemo(
    () =>
      (data?.scanTrend || []).map((item) => ({
        ...item,
        avgBMI: asNumber(item.avgBMI),
        avgScore: asNumber(item.avgScore),
        avgWeight: asNumber(item.avgWeight),
      })),
    [data]
  );

  if (!canView) {
    return <PermissionDenied title={t.states.permissionDenied} message={t.states.cannotViewDashboard} />;
  }

  return (
    <div className="configuration health-page health-dashboard-page" dir={isRtl ? "rtl" : "ltr"}>
      <div className="health-page-shell">
        <HealthPageHeader title={t.pages.dashboard} subtitle={t.descriptions.dashboard} />

        <div className="health-toolbar">
          <DatePicker
            label={t.actions.from}
            value={from}
            onChange={(newValue) => setFrom(newValue)}
            format="DD/MM/YYYY"
            slotProps={{ textField: { size: "small" } }}
            renderInput={(params) => <TextField {...params} size="small" />}
          />
          <DatePicker
            label={t.actions.to}
            value={to}
            onChange={(newValue) => setTo(newValue)}
            format="DD/MM/YYYY"
            slotProps={{ textField: { size: "small" } }}
            renderInput={(params) => <TextField {...params} size="small" />}
          />
          <button
            className="health-secondary-btn"
            type="button"
            onClick={() => {
              setFrom(null);
              setTo(null);
            }}
          >
            {t.actions.clearDates}
          </button>
          <span className="health-chip">{t.states.latestOnly}</span>
          {data?.scopeFallback ? <span className="health-chip">{t.states.scopeFallback}</span> : null}
        </div>

        {loading ? (
          <Spinner />
        ) : data ? (
          <>
            <div className="health-summary-grid">
              <SummaryCard label={t.labels.users} value={data.usersCount || 0} note={t.states.latestOnly} />
              <SummaryCard label={t.labels.scans} value={data.scansCount || 0} />
              <SummaryCard label={t.labels.avgBmi} value={roundValue(data.avgBMI)} color={getBMIColor(data.avgBMI)} />
              <SummaryCard label={t.labels.avgScore} value={roundValue(data.avgScore)} />
            </div>

            <div className="health-grid">
              <Section title={t.sections.averages}>
                <Field label={t.labels.age} value={roundValue(data.avgAge)} />
                <Field label={t.labels.bodyAge} value={roundValue(data.avgBodyAge)} />
                <Field label={t.labels.weight} value={roundValue(data.avgWeight)} />
                <Field label={t.labels.height} value={roundValue(data.avgHeight)} />
              </Section>

              <Section title={t.sections.vitals}>
                <Field label={t.labels.heartRate} value={roundValue(data.avgHeartRate)} />
                <Field label={t.labels.maxPressure} value={roundValue(data.avgBloodMax)} />
                <Field label={t.labels.minPressure} value={roundValue(data.avgBloodMin)} />
                <Field label={t.labels.oxygen} value={formatPercent(data.avgBloodOxygen)} />
              </Section>

              <Section title={t.sections.benefitStats}>
                <Field label={t.labels.bmr} value={roundValue(data.avgBmr)} />
                <Field label={t.labels.caloriesInput} value={roundValue(data.avgCaloriesInput)} />
                <Field label={t.labels.perfectWeight} value={roundValue(data.avgPerfectWeight)} />
                <Field label={t.labels.idealWeightGap} value={roundValue(data.idealWeightGap)} />
                <Field label={t.labels.vitalCapacity} value={roundValue(data.avgVitalCapacity)} />
                <Field label={t.labels.latestScan} value={formatDateTime(data.latestScanAt)} />
              </Section>

              <Section title={t.sections.activityGoals}>
                <Field label={t.labels.aerobic} value={roundValue(data.avgAerobicGoal)} />
                <Field label={t.labels.anaerobic} value={roundValue(data.avgAnaGoal)} />
                <Field label={t.labels.endurance} value={roundValue(data.avgEnduGoal)} />
                <Field label={t.labels.sportGoal} value={roundValue(data.avgSportGoal)} />
                <Field label={t.labels.balance} value={roundValue(data.avgBalance)} />
                <Field label={t.labels.balanceAngle} value={formatPercent(data.avgBalanceAngle)} />
              </Section>

              <ChartCard title={t.sections.composition} wide>
                <SimpleBarChart data={compositionData} color="var(--health-accent-color)" valueName={t.labels.value} />
              </ChartCard>

              <ChartCard title={t.sections.bmiDistribution}>
                <SimpleBarChart data={bmiData} colors={COLORS} valueName={t.labels.users} />
              </ChartCard>

              <ChartCard title={t.sections.riskOverview}>
                <SimpleBarChart
                  data={riskData}
                  colors={[
                    "var(--health-success-color)",
                    "var(--health-warning-color)",
                    "var(--health-danger-color)",
                  ]}
                  valueName={t.labels.users}
                />
              </ChartCard>

              <ChartCard title={t.sections.benefitStats} wide>
                <SimpleBarChart data={benefitData} color="var(--health-info-color)" valueName={t.labels.value} />
              </ChartCard>

              <ChartCard title={t.sections.activityGoals}>
                <SimpleBarChart data={goalsData} color="var(--health-success-color)" valueName={t.labels.value} />
              </ChartCard>

              <ChartCard title={t.sections.segmentComposition} wide>
                <GroupedBarChart
                  data={segmentData}
                  bars={[
                    { key: "fat", name: t.categories.fat, color: "var(--health-danger-color)" },
                    { key: "muscle", name: t.categories.muscle, color: "var(--health-success-color)" },
                  ]}
                />
              </ChartCard>

              <ChartCard title={t.sections.postureRisks}>
                <SimpleBarChart data={riskAverageData} color="var(--health-warning-color)" valueName={t.labels.value} />
              </ChartCard>

              <ChartCard title={t.sections.scoreDistribution}>
                <SimpleBarChart
                  data={scoreData}
                  colors={[
                    "var(--health-success-color)",
                    "var(--health-info-color)",
                    "var(--health-danger-color)",
                  ]}
                  valueName={t.labels.users}
                />
              </ChartCard>

              <ChartCard title={t.sections.metricCoverage}>
                <SimpleBarChart data={coverageData} color="var(--health-accent-color)" valueName={t.labels.users} />
              </ChartCard>

              <ChartCard title={t.sections.scanTrend} wide>
                <TrendChart data={trendData} labels={{ scans: t.labels.scans, bmi: t.columns.bmi, score: t.labels.avgScore }} />
              </ChartCard>
            </div>
          </>
        ) : (
          <div className="health-empty-state">{t.states.noData}</div>
        )}
      </div>
    </div>
  );
};

const ChartCard = ({ title, children, wide }) => (
  <section className={`health-card ${wide ? "health-card-wide" : ""}`}>
    <h3>{title}</h3>
    {children}
  </section>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="health-chart-tooltip">
      <div className="health-chart-tooltip-title">{label}</div>
      {payload.map((entry) => (
        <div className="health-chart-tooltip-row" key={entry.dataKey || entry.name}>
          <span className="health-chart-tooltip-dot" style={{ background: entry.color }} />
          <span>{entry.name}</span>
          <strong>{roundValue(entry.value)}</strong>
        </div>
      ))}
    </div>
  );
};

const SimpleBarChart = ({ data, color, colors, valueName }) => (
  <div className="health-chart">
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 8, right: 10, left: -14, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--health-chart-grid)" />
        <XAxis dataKey="name" tick={{ fill: "var(--health-muted-color)", fontSize: "0.75em" }} />
        <YAxis tick={{ fill: "var(--health-muted-color)", fontSize: "0.75em" }} />
        <Tooltip cursor={{ fill: "var(--health-chart-hover)" }} content={<CustomTooltip />} />
        <Legend wrapperStyle={{ color: "var(--health-muted-color)", fontSize: "0.78em" }} />
        <Bar
          dataKey="value"
          name={valueName}
          radius={[8, 8, 0, 0]}
          fill={color || "var(--health-accent-color)"}
          activeBar={{ stroke: "var(--health-text-color)", strokeWidth: 1 }}
        >
          {colors ? data.map((entry, index) => <Cell key={entry.name} fill={colors[index % colors.length]} />) : null}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </div>
);

const GroupedBarChart = ({ data, bars }) => (
  <div className="health-chart">
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 8, right: 10, left: -14, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--health-chart-grid)" />
        <XAxis dataKey="name" tick={{ fill: "var(--health-muted-color)", fontSize: "0.75em" }} />
        <YAxis tick={{ fill: "var(--health-muted-color)", fontSize: "0.75em" }} />
        <Tooltip cursor={{ fill: "var(--health-chart-hover)" }} content={<CustomTooltip />} />
        <Legend wrapperStyle={{ color: "var(--health-muted-color)", fontSize: "0.78em" }} />
        {bars.map((bar) => (
          <Bar
            key={bar.key}
            dataKey={bar.key}
            name={bar.name}
            fill={bar.color}
            radius={[8, 8, 0, 0]}
            activeBar={{ stroke: "var(--health-text-color)", strokeWidth: 1 }}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  </div>
);

const TrendChart = ({ data, labels }) => (
  <div className="health-chart">
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 8, right: 16, left: -14, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--health-chart-grid)" />
        <XAxis dataKey="date" tick={{ fill: "var(--health-muted-color)", fontSize: "0.75em" }} />
        <YAxis tick={{ fill: "var(--health-muted-color)", fontSize: "0.75em" }} />
        <Tooltip cursor={{ stroke: "var(--health-accent-color)", strokeWidth: 1 }} content={<CustomTooltip />} />
        <Legend wrapperStyle={{ color: "var(--health-muted-color)", fontSize: "0.78em" }} />
        <Line
          type="monotone"
          dataKey="scans"
          name={labels.scans}
          stroke="var(--health-accent-color)"
          strokeWidth={3}
          dot={{ r: 3, fill: "var(--health-accent-color)" }}
          activeDot={{ r: 6 }}
        />
        <Line
          type="monotone"
          dataKey="avgBMI"
          name={labels.bmi}
          stroke="var(--health-warning-color)"
          strokeWidth={2}
          dot={{ r: 3, fill: "var(--health-warning-color)" }}
          activeDot={{ r: 6 }}
        />
        <Line
          type="monotone"
          dataKey="avgScore"
          name={labels.score}
          stroke="var(--health-success-color)"
          strokeWidth={2}
          dot={{ r: 3, fill: "var(--health-success-color)" }}
          activeDot={{ r: 6 }}
        />
      </LineChart>
    </ResponsiveContainer>
  </div>
);
